/**
 * DurataFields — F-003.2 Phase 2 (IMPL-P2-005) + F-005 Phase 5 (IMPL-P5-004).
 *
 * Campi: tipologia (select), durataMesi (min/max dinamici per tipologia),
 *        dataStipula (date, max=oggi), regimeFiscale (select).
 *
 * Native <select> con classi Tailwind (shadcn Select non ancora installato).
 * Se shadcn Select viene aggiunto in futuro, sostituire il native select
 * senza cambiare la logica Controller/RHF.
 *
 * F-005: ring amber + sr-only hint su `dataStipula` e `durataMesi`
 * (BLOCKED_REASON_FIELDS.CONTRATTO_INCOMPLETO).
 */
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import type { ContractDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useErrorFields } from '../../hooks/useErrorFields';
import {
  CONTESTED_HINT_TEXT,
  CONTESTED_RING_CLASS,
  contestedHintId,
} from './contestedFieldUtils';

// Durata ranges per tipologia (allineati con DURATA_PER_TIPOLOGIA in contract.ts)
const DURATA_RANGES: Record<string, { min: number; max?: number; label: string }> = {
  '4+4': { min: 48, label: '4+4 (locazione ordinaria)' },
  '3+2': { min: 36, label: '3+2 (canone concordato)' },
  transitorio: { min: 1, max: 18, label: 'Transitorio (max 18 mesi)' },
  studente: { min: 6, max: 36, label: 'Studenti universitari (6-36 mesi)' },
  commerciale: { min: 72, label: 'Commerciale (min 6 anni)' },
  comodato: { min: 1, label: 'Comodato' },
};

const REGIME_FISCALE_OPTIONS = [
  { value: 'CEDOLARE_SECCA', label: 'Cedolare secca' },
  { value: 'ORDINARIO', label: 'Regime ordinario IRPEF' },
  { value: 'CONCORDATO', label: 'Canone concordato' },
];

const selectClassName =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const todayISO = new Date().toISOString().slice(0, 10);

export function DurataFields() {
  const {
    control,
    formState: { errors },
  } = useFormContext<ContractDraft>();
  const tipologia = useWatch({ control, name: 'tipologia' });
  const range = tipologia ? DURATA_RANGES[tipologia] : undefined;

  const errorFields = useErrorFields();
  const isDataStipulaContested = errorFields.includes('dataStipula');
  const isDurataMesiContested = errorFields.includes('durataMesi');

  return (
    <div className="space-y-4">
      {/* Tipologia contrattuale */}
      <div className="space-y-1">
        <Label htmlFor="tipologia">Tipologia contrattuale *</Label>
        <Controller
          name="tipologia"
          control={control}
          render={({ field }) => (
            <select
              id="tipologia"
              className={selectClassName}
              {...field}
              value={field.value ?? ''}
              aria-invalid={!!errors.tipologia}
              aria-describedby={errors.tipologia ? 'tipologia-error' : undefined}
            >
              <option value="" disabled>
                Seleziona tipologia…
              </option>
              {Object.entries(DURATA_RANGES).map(([val, { label }]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
          )}
        />
        {errors.tipologia && (
          <p id="tipologia-error" className="text-xs text-destructive">
            {errors.tipologia.message}
          </p>
        )}
      </div>

      {/* Durata mesi */}
      <div className="space-y-1">
        <Label htmlFor="durata-mesi">
          Durata (mesi) *
          {range && (
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              {range.max ? `(${range.min}–${range.max} mesi)` : `(min ${range.min} mesi)`}
            </span>
          )}
        </Label>
        <Controller
          name="durataMesi"
          control={control}
          render={({ field }) => (
            <Input
              id="durata-mesi"
              type="number"
              min={range?.min ?? 1}
              max={range?.max}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(
                  Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber,
                )
              }
              aria-invalid={!!errors.durataMesi}
              aria-describedby={
                errors.durataMesi
                  ? 'durata-mesi-error'
                  : isDurataMesiContested
                    ? contestedHintId('durataMesi')
                    : undefined
              }
              className={cn(isDurataMesiContested && CONTESTED_RING_CLASS)}
            />
          )}
        />
        {errors.durataMesi && (
          <p id="durata-mesi-error" className="text-xs text-destructive">
            {errors.durataMesi.message}
          </p>
        )}
        {isDurataMesiContested && (
          <span id={contestedHintId('durataMesi')} className="sr-only">
            {CONTESTED_HINT_TEXT}
          </span>
        )}
      </div>

      {/* Data stipula */}
      <div className="space-y-1">
        <Label htmlFor="data-stipula">Data di stipula *</Label>
        <Controller
          name="dataStipula"
          control={control}
          render={({ field }) => {
            // ContractDraft.dataStipula è Date | undefined (z.coerce.date in deepPartial),
            // ma RHF può ricevere stringhe ISO da defaultValues/autosave restore.
            const raw: unknown = field.value;
            const dateValue =
              raw instanceof Date
                ? raw.toISOString().slice(0, 10)
                : typeof raw === 'string'
                  ? raw.slice(0, 10)
                  : '';
            return (
              <Input
                id="data-stipula"
                type="date"
                max={todayISO}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
                value={dateValue}
                onChange={(e) =>
                  field.onChange(e.target.value ? new Date(e.target.value) : undefined)
                }
                aria-invalid={!!errors.dataStipula}
                aria-describedby={
                  errors.dataStipula
                    ? 'data-stipula-error'
                    : isDataStipulaContested
                      ? contestedHintId('dataStipula')
                      : undefined
                }
                className={cn(isDataStipulaContested && CONTESTED_RING_CLASS)}
              />
            );
          }}
        />
        {errors.dataStipula && (
          <p id="data-stipula-error" className="text-xs text-destructive">
            {errors.dataStipula.message}
          </p>
        )}
        {isDataStipulaContested && (
          <span id={contestedHintId('dataStipula')} className="sr-only">
            {CONTESTED_HINT_TEXT}
          </span>
        )}
      </div>

      {/* Regime fiscale */}
      <div className="space-y-1">
        <Label htmlFor="regime-fiscale">Regime fiscale *</Label>
        <Controller
          name="regimeFiscale"
          control={control}
          render={({ field }) => (
            <select
              id="regime-fiscale"
              className={selectClassName}
              {...field}
              value={field.value ?? ''}
              aria-invalid={!!errors.regimeFiscale}
              aria-describedby={errors.regimeFiscale ? 'regime-fiscale-error' : undefined}
            >
              <option value="" disabled>
                Seleziona regime…
              </option>
              {REGIME_FISCALE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          )}
        />
        {errors.regimeFiscale && (
          <p id="regime-fiscale-error" className="text-xs text-destructive">
            {errors.regimeFiscale.message}
          </p>
        )}
      </div>
    </div>
  );
}
